/*
 * A copy of the driver application's module of the same name, kept byte-for-byte except for the
 * storage keys and the prose that named a courier.
 *
 * Nothing in it is design content: it is the OpenID Connect authorization-code flow with PKCE, the
 * token refresh, the expiry and the claim reading, and a rider's phone needs all of it exactly as a
 * courier's does. It is a copy rather than a shared package for the reason
 * `docs/adr/0021-two-consoles-and-no-shared-shell.md` recorded when the second console arrived: a
 * package in `packages/` could not be built by `npm run check` before its consumers, and that would
 * be worse than this duplication. This is the **fourth** copy, which is worth saying out loud: that
 * record's condition for extracting the protocol into a package was "a third console", and the
 * channels have now passed it twice. The extraction is recorded in `docs/OPEN_WORK.md` rather than
 * taken here, because it belongs to `packages/` and this agent owns none of it.
 *
 * `docs/adr/0022-keycloaks-own-page-is-the-sign-in-page.md` records the decision `arrival.tsx`
 * takes, including the two unauthenticated arrivals that must *not* redirect.
 */

import { useCallback, useEffect, useSyncExternalStore } from "react";

import type { AuthenticationState, SessionAuthority } from "./authority.ts";

/**
 * The shortest a renewal timer is allowed to be.
 *
 * Without a floor this is a tight loop. A renewal that fails because the provider is briefly
 * unreachable leaves the token exactly as aged as it was, so the next delay would be zero, and the
 * console would hammer the token endpoint for as long as the outage lasted. Five seconds is long
 * enough to be a retry and short enough that an operator does not notice the recovery.
 */
const MINIMUM_RENEWAL_DELAY_MS = 5_000;

/**
 * Subscribe a component to who is signed in, and keep the token ahead of its expiry.
 *
 * Two things, because they are one thing: the component tree and the renewal timer have to agree
 * about the session, and the way to guarantee that is for the timer to live where the state is read.
 *
 * The renewal is belt as well as braces. `SessionAuthority.accessToken()` already refreshes a token
 * that is about to expire before a request is allowed to carry it, so nothing depends on this timer
 * firing. What it adds is the idle case: a console left open on a board that is not polling would
 * otherwise let its refresh token sit until the provider's idle timeout had passed, and the operator
 * would be signed out by having done nothing.
 */
export function useAuthentication(authority: SessionAuthority): AuthenticationState {
  const subscribe = useCallback(
    (listener: () => void) => authority.subscribe(listener),
    [authority],
  );
  const state = useSyncExternalStore(
    subscribe,
    () => authority.getState(),
    () => authority.getState(),
  );

  useEffect(() => {
    if (state.kind !== "signed_in") return;
    const until = authority.millisecondsUntilRenewal();
    if (until === undefined) return;
    const timer = setTimeout(
      () => {
        void authority.renew();
      },
      Math.max(MINIMUM_RENEWAL_DELAY_MS, until),
    );
    return (): void => {
      clearTimeout(timer);
    };
    // `state` rather than `state.kind`: every renewal publishes a new session, which is what
    // re-arms this timer for the next one.
  }, [authority, state]);

  return state;
}
